import React, { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import BlogImagePicker from "./BlogImagePicker";
import { previewMdComponents } from "./BlogPreviewRenderer";
import useVenuesById from "./useVenuesById";
import {
  QuestionMarkCircleIcon,
  BookOpenIcon,
} from "@heroicons/react/20/solid";

/**
 * Split markdown editor: textarea on the left, live brand-styled preview on
 * the right. The preview uses the exact same markdown renderer/classes as
 * the public Next.js blog post body (via previewMdComponents).
 * Incluye los bloques ```venues, que se ven como tarjetas mientras se escribe.
 *
 * Props:
 *   postId   - string: used as the Storage folder for uploaded images
 *   value    - current markdown string
 *   onChange - (newValue: string) => void
 *   label    - optional label above the editor
 */
export default function MarkdownEditor({ postId, value = "", onChange, label, locale = "es" }) {
  // Venues para las tarjetas de los bloques ```venues de la vista previa.
  const venuesById = useVenuesById(true);
  const textareaRef = useRef(null);
  const previewRef = useRef(null);
  // Tracks which pane is driving the current scroll sync, so the programmatic
  // scroll on the other pane doesn't bounce back and create an infinite loop.
  const syncSourceRef = useRef(null);

  function handleEditorScroll() {
    const editor = textareaRef.current;
    const preview = previewRef.current;
    if (!editor || !preview) return;
    if (syncSourceRef.current === "preview") return;

    const editorMax = editor.scrollHeight - editor.clientHeight;
    const previewMax = preview.scrollHeight - preview.clientHeight;
    if (editorMax <= 0 || previewMax <= 0) return;

    const ratio = editor.scrollTop / editorMax;
    syncSourceRef.current = "editor";
    preview.scrollTop = ratio * previewMax;
    requestAnimationFrame(() => {
      syncSourceRef.current = null;
    });
  }

  function handlePreviewScroll() {
    const editor = textareaRef.current;
    const preview = previewRef.current;
    if (!editor || !preview) return;
    if (syncSourceRef.current === "editor") return;

    const editorMax = editor.scrollHeight - editor.clientHeight;
    const previewMax = preview.scrollHeight - preview.clientHeight;
    if (editorMax <= 0 || previewMax <= 0) return;

    const ratio = preview.scrollTop / previewMax;
    syncSourceRef.current = "preview";
    editor.scrollTop = ratio * editorMax;
    requestAnimationFrame(() => {
      syncSourceRef.current = null;
    });
  }

  function insertAtCursor(snippet) {
    const el = textareaRef.current;
    if (!el) {
      onChange(`${value}\n\n${snippet}\n`);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const padded = `${before}${before && !before.endsWith("\n\n") ? "\n\n" : ""}${snippet}${after && !after.startsWith("\n\n") ? "\n\n" : ""}${after}`;
    onChange(padded);
    // Restore focus on next tick, cursor after the snippet
    setTimeout(() => {
      const pos = before.length + snippet.length + 2;
      el.focus();
      el.setSelectionRange(pos, pos);
    }, 0);
  }

  return (
    <div className="mb-4">
      {label && <label className="block mb-1 font-medium">{label}</label>}

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <BlogImagePicker postId={postId} onInsert={insertAtCursor} />
        <ImageSizeHint />
        <MarkdownCheatSheet />
        <span className="text-xs text-gray-500">
          Usa markdown estándar. Las imágenes se insertan con su tamaño.
        </span>
      </div>

      <div className="grid grid-cols-1 gap-0 divide-y divide-gray-200 rounded-md border border-gray-300 lg:grid-cols-2 lg:divide-x lg:divide-y-0">
        {/* Editor */}
        <div className="flex flex-col">
          <div className="border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Editor
          </div>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onScroll={handleEditorScroll}
            rows={20}
            className="h-full max-h-[40rem] w-full resize-y overflow-auto border-0 bg-white p-4 font-mono text-sm focus:outline-none focus:ring-0"
            placeholder={"# Título\n\nEscribe tu post en **markdown**..."}
          />
        </div>

        {/* Live brand-styled preview — same markdown components as the live site */}
        <div className="flex flex-col bg-white">
          <div className="border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Vista previa
          </div>
          <div
            ref={previewRef}
            onScroll={handlePreviewScroll}
            className="max-h-[40rem] overflow-auto p-6"
          >
            {value.trim() ? (
              <article className="prose prose-lg max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={previewMdComponents({ venuesById, locale })}
                >
                  {value}
                </ReactMarkdown>
              </article>
            ) : (
              <p className="text-gray-400">Nada para mostrar todavía.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Hover/focus tooltip explaining the available image size keywords.
 * Pure CSS via Tailwind `group-hover` + `focus-within`, so it works with
 * both mouse hover and keyboard focus on the ? icon.
 */
function ImageSizeHint() {
  return (
    <div className="group relative inline-flex">
      <button
        type="button"
        aria-label="Ver tamaños de imagen disponibles"
        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-[#1b4d2e] focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]/30"
      >
        <QuestionMarkCircleIcon className="h-5 w-5" />
      </button>
      <div className="invisible absolute left-0 top-full z-20 mt-2 w-80 origin-top-left scale-95 rounded-lg border border-gray-200 bg-white p-4 text-left text-xs text-gray-700 opacity-0 shadow-lg transition-all duration-150 group-hover:visible group-hover:scale-100 group-hover:opacity-100 group-focus-within:visible group-focus-within:scale-100 group-focus-within:opacity-100">
        <p className="mb-2 text-sm font-semibold text-[#1b4d2e]">
          Tamaños de imagen
        </p>
        <p className="mb-3 text-gray-600">
          Sintaxis:{" "}
          <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px]">
            ![texto alt|tamaño](url)
          </code>
        </p>
        <ul className="space-y-1.5">
          <li className="flex items-baseline gap-2">
            <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-[#1b4d2e]">
              small
            </code>
            <span>Pequeña, centrada (≈320px)</span>
          </li>
          <li className="flex items-baseline gap-2">
            <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-[#1b4d2e]">
              medium
            </code>
            <span>Mediana, centrada (≈448px)</span>
          </li>
          <li className="flex items-baseline gap-2">
            <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-[#1b4d2e]">
              large
            </code>
            <span>Grande, centrada (≈672px)</span>
          </li>
          <li className="flex items-baseline gap-2">
            <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-[#1b4d2e]">
              full
            </code>
            <span>Ancho completo (por defecto)</span>
          </li>
        </ul>
        <p className="mt-3 border-t border-gray-100 pt-2 text-gray-500">
          Cambia el valor entre <code className="font-mono">|</code> y{" "}
          <code className="font-mono">]</code> en el editor para ajustar el
          tamaño sin volver a subir la imagen.
        </p>
      </div>
    </div>
  );
}

/**
 * Markdown cheat sheet — hover/focus tooltip listing the most useful
 * markdown syntax supported by the blog editor (react-markdown + remark-gfm).
 * Each snippet is click-to-copy.
 */
const CHEAT_ROWS = [
  { syntax: "# Título", desc: "Encabezado H1" },
  { syntax: "## Subtítulo", desc: "Encabezado H2" },
  { syntax: "### Sección", desc: "Encabezado H3" },
  { syntax: "**negrita**", desc: "Texto en negrita" },
  { syntax: "*cursiva*", desc: "Texto en cursiva" },
  { syntax: "~~tachado~~", desc: "Texto tachado" },
  { syntax: "[texto](url)", desc: "Enlace" },
  { syntax: "- item", desc: "Lista con viñetas" },
  { syntax: "1. item", desc: "Lista numerada" },
  { syntax: "- [ ] tarea", desc: "Lista de tareas" },
  { syntax: "> cita", desc: "Cita / blockquote" },
  { syntax: "---", desc: "Separador horizontal" },
];

const TABLE_SNIPPET = `| Col A | Col B |
| ----- | ----- |
| dato  | dato  |`;

// Bloque de venues: se renderiza como tarjetas en el sitio público.
// Ver homesite/venueboard-main/src/lib/venueEmbeds.js
const VENUES_SNIPPET = `\`\`\`venues
jardin-ziba | Nota opcional del autor
casa-orizaba
\`\`\``;

function MarkdownCheatSheet() {
  const [copied, setCopied] = useState(null);

  function handleCopy(text) {
    if (!navigator.clipboard) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(text);
        setTimeout(() => setCopied((c) => (c === text ? null : c)), 1200);
      })
      .catch(() => {});
  }

  return (
    <div className="group relative inline-flex">
      <button
        type="button"
        aria-label="Ver referencia de markdown"
        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-[#1b4d2e] focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]/30"
      >
        <BookOpenIcon className="h-5 w-5" />
      </button>
      <div className="absolute left-0 top-full z-20 mt-2 w-[26rem] origin-top-left scale-95 rounded-lg border border-gray-200 bg-white p-4 text-left text-xs text-gray-700 opacity-0 shadow-lg transition-all duration-150 group-hover:scale-100 group-hover:opacity-100 group-focus-within:scale-100 group-focus-within:opacity-100 invisible group-hover:visible group-focus-within:visible">
        <p className="mb-2 text-sm font-semibold text-[#1b4d2e]">
          Referencia rápida de Markdown
        </p>
        <p className="mb-3 text-gray-500">
          Haz click en cualquier snippet para copiarlo, luego pégalo en el
          editor.
        </p>
        <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2">
          {CHEAT_ROWS.map(({ syntax, desc }) => (
            <div key={syntax} className="flex flex-col">
              <button
                type="button"
                onClick={() => handleCopy(syntax)}
                className="group/snip flex items-center justify-between gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-left font-mono text-[11px] text-[#1b4d2e] transition hover:bg-[#1b4d2e]/10"
                title="Click para copiar"
              >
                <span className="truncate">{syntax}</span>
                <span className="text-[9px] uppercase tracking-wide text-gray-400 group-hover/snip:text-[#1b4d2e]">
                  {copied === syntax ? "¡Copiado!" : "copiar"}
                </span>
              </button>
              <span className="mt-0.5 text-[11px] text-gray-600">{desc}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 border-t border-gray-100 pt-2">
          <div className="mb-1 flex items-center justify-between">
            <p className="font-semibold text-gray-600">Tarjetas de venue</p>
            <button
              type="button"
              onClick={() => handleCopy(VENUES_SNIPPET)}
              className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 transition hover:text-[#1b4d2e]"
            >
              {copied === VENUES_SNIPPET ? "¡Copiado!" : "copiar"}
            </button>
          </div>
          <pre
            onClick={() => handleCopy(VENUES_SNIPPET)}
            className="cursor-pointer rounded bg-gray-100 p-2 font-mono text-[11px] text-[#1b4d2e] transition hover:bg-[#1b4d2e]/10"
            title="Click para copiar"
          >
{VENUES_SNIPPET}
          </pre>
          <p className="mt-1 text-[11px] text-gray-500">
            Un venue por línea, con el id de su URL (/venues/<strong>este-id</strong>).
            El orden que escribas es el orden en que se muestran. La nota
            después de <code className="font-mono">|</code> es opcional.
          </p>
        </div>

        <div className="mt-3 border-t border-gray-100 pt-2">
          <div className="mb-1 flex items-center justify-between">
            <p className="font-semibold text-gray-600">Tablas</p>
            <button
              type="button"
              onClick={() => handleCopy(TABLE_SNIPPET)}
              className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 transition hover:text-[#1b4d2e]"
            >
              {copied === TABLE_SNIPPET ? "¡Copiado!" : "copiar"}
            </button>
          </div>
          <pre
            onClick={() => handleCopy(TABLE_SNIPPET)}
            className="cursor-pointer rounded bg-gray-100 p-2 font-mono text-[11px] text-[#1b4d2e] transition hover:bg-[#1b4d2e]/10"
            title="Click para copiar"
          >
{TABLE_SNIPPET}
          </pre>
        </div>
      </div>
    </div>
  );
}
